import axios from 'axios';
import { API } from '../../../config/api';

// Work hours configuration
export const WORK_HOURS = {
  morning: { start: "08:30", end: "12:30" },
  afternoon: { start: "13:30", end: "17:30" },
  lunchBreak: { start: "12:30", end: "13:30", duration: 60 },
  lunchWindow: { start: "12:00", end: "14:00" },
  // Free commute allowance: first/last hour of the day is considered travel time
  commuteAllowance: 60, // minutes
};

// Intervention types with colors
export const interventionTypes = [
  { value: "installation", label: "Installazione", color: "#3B82F6" },
  { value: "cq", label: "CQ (Controllo Qualità)", color: "#22C55E" },
  { value: "cq_pf", label: "CQ PF (Post Furto)", color: "#F97316" },
  { value: "ca", label: "CA (Controllo Anomalia)", color: "#EAB308" },
  { value: "training", label: "Formazione", color: "#A855F7" },
  { value: "uninstall", label: "Disinstallazione", color: "#6B7280" },
  { value: "absence", label: "Assenza", color: "#94A3B8" },
  { value: "other", label: "Altro", color: "#EC4899" },
];

// Absence subtypes (shown only if intervention_type === 'absence')
export const absenceTypes = [
  { value: "vacation", label: "Ferie" },
  { value: "sick_leave", label: "Malattia" },
  { value: "holiday", label: "Festività" },
  { value: "other", label: "Altro" },
];

// Device types with installation times.
// IMPORTANT: only SINGLE devices are selectable for new appointments.
// COMBO entries (e.g. "SVR + ASSIST") are kept as DEPRECATED so existing appointments still render their label.
// To compose multiple devices on the same vehicle, use the "Job aggiuntivo" feature.
const _SINGLE_DEVICE_TYPES = [
  { value: "svr", label: "SVR", duration: 60, durationLabel: "1 ora" },
  { value: "vlu6r3", label: "VLU6R3", duration: 90, durationLabel: "1:30 ore" },
  { value: "lmu3040", label: "LMU3040", duration: 30, durationLabel: "30 min" },
  { value: "lmu2630", label: "LMU2630", duration: 60, durationLabel: "1 ora" },
  { value: "lmu2630_batt", label: "LMU2630 batt", duration: 30, durationLabel: "30 min" },
  { value: "lmu2630_blocco", label: "LMU2630 blocco", duration: 90, durationLabel: "1:30 ore" },
  { value: "can_badge", label: "CAN & Badge Reader", duration: 180, durationLabel: "3 ore" },
  { value: "assist_b", label: "ASSIST-B", duration: 30, durationLabel: "30 min" },
  { value: "atu1300", label: "ATU1300", duration: 30, durationLabel: "30 min" },
  { value: "atu1650", label: "ATU1650", duration: 30, durationLabel: "30 min" },
  { value: "pacchetto_jlr", label: "PACCHETTO JLR", duration: 210, durationLabel: "3:30 ore" },
  { value: "pqs", label: "PQS", duration: 30, durationLabel: "30 min" },
  { value: "ttu2830", label: "TTU2830", duration: 60, durationLabel: "1 ora" },
  { value: "ttu2830_blocco", label: "TTU2830 blocco", duration: 90, durationLabel: "1:30 ore" },
];

const _LEGACY_COMBO_DEVICE_TYPES = [
  { value: "svr_lmu2630", label: "SVR + LMU2630", duration: 120, durationLabel: "2 ore", deprecated: true },
  { value: "svr_lmu3040", label: "SVR + LMU3040", duration: 90, durationLabel: "1:30 ore", deprecated: true },
  { value: "svr_assist", label: "SVR + ASSIST-B/ATU1300", duration: 90, durationLabel: "1:30 ore", deprecated: true },
  { value: "svr_pq", label: "SVR + PQ SVR", duration: 120, durationLabel: "2 ore", deprecated: true },
  { value: "svr_pqs", label: "SVR + PQS", duration: 90, durationLabel: "1:30 ore", deprecated: true },
  { value: "svr_assist_pqs", label: "SVR + ASSIST/ATU + PQS", duration: 120, durationLabel: "2 ore", deprecated: true },
  { value: "svr_pq_pqs", label: "SVR + PQ SVR + PQS", duration: 150, durationLabel: "2:30 ore", deprecated: true },
  { value: "svr_lmu2630_blocco", label: "SVR + LMU2630 blocco", duration: 150, durationLabel: "2:30 ore", deprecated: true },
  { value: "svr_lmu2630_assist", label: "SVR + LMU2630 + ASSIST-B/ATU1300", duration: 150, durationLabel: "2:30 ore", deprecated: true },
  { value: "svr_lmu2630_blocco_assist", label: "SVR + LMU2630 blocco + ASSIST-B/ATU1300", duration: 180, durationLabel: "3 ore", deprecated: true },
  { value: "svr_lmu3040_doppio_assist", label: "SVR + LMU3040 + doppio ASSIST-B/ATU1300", duration: 150, durationLabel: "2:30 ore", deprecated: true },
  { value: "svr_lmu2630_doppio_assist", label: "SVR + LMU2630 + doppio ASSIST-B/ATU1300", duration: 180, durationLabel: "3 ore", deprecated: true },
  { value: "vlu6r3_lmu3040", label: "VLU6R3 + LMU3040", duration: 120, durationLabel: "2 ore", deprecated: true },
];

// `deviceTypes` exposes only the single devices (the only ones selectable on new appointments).
export const deviceTypes = _SINGLE_DEVICE_TYPES;

// `allDeviceTypes` includes legacy combos for rendering existing appointments only.
export const allDeviceTypes = [..._SINGLE_DEVICE_TYPES, ..._LEGACY_COMBO_DEVICE_TYPES];

// Vehicle categories:
//  - Auto: 0 (default)
//  - Supercar: +30 min
//  - Moto: +60 min (aggiornato su richiesta Veronica del 01/07/2026)
//  - Scooter: +30 min (nuovo, aggiunto su richiesta Veronica del 01/07/2026)
export const vehicleCategories = [
  { value: "auto", label: "Auto", extraMinutes: 0 },
  { value: "supercar", label: "Supercar", extraMinutes: 30 },
  { value: "moto", label: "Moto", extraMinutes: 60 },
  { value: "scooter", label: "Scooter", extraMinutes: 30 },
];

// Helper function to format minutes into hours and minutes
export const formatTravelTime = (minutes) => {
  if (!minutes || minutes <= 0) return "0 min";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins.toString().padStart(2, '0')}min`;
};

// Helper to convert time string to minutes from midnight
export const timeToMinutes = (time) => {
  if (!time) return 0;
  const [hours, mins] = time.split(':').map(Number);
  return hours * 60 + mins;
};

// Helper to convert minutes from midnight to time string
export const minutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Check if time is in lunch break (12:30-13:30)
export const isInLunchBreak = (time) => {
  const mins = timeToMinutes(time);
  return mins >= timeToMinutes("12:30") && mins < timeToMinutes("13:30");
};

// Check if an appointment [start, start+duration] overlaps the standard lunch break (12:30-13:30).
// This catches cases where the START is before 12:30 but the appointment EXTENDS past 12:30,
// which would leave the technician working through lunch.
export const crossesLunchBreak = (startTime, durationMinutes) => {
  if (!startTime || !durationMinutes || durationMinutes <= 0) return false;
  const start = timeToMinutes(startTime);
  const end = start + durationMinutes;
  const lunchStart = timeToMinutes("12:30");
  const lunchEnd = timeToMinutes("13:30");
  return start < lunchEnd && end > lunchStart;
};

// Check if time is outside work hours
export const isOutsideWorkHours = (time) => {
  const mins = timeToMinutes(time);
  const inMorning = mins >= timeToMinutes("08:30") && mins < timeToMinutes("12:30");
  const inAfternoon = mins >= timeToMinutes("13:30") && mins <= timeToMinutes("17:30");
  return !inMorning && !inAfternoon;
};

// Calculate if travel crosses lunch window and needs buffer
export const calculateLunchBuffer = (departureTime, travelMinutes) => {
  const departMins = timeToMinutes(departureTime);
  const arrivalMins = departMins + travelMinutes;
  const lunchStart = timeToMinutes("12:00");
  const lunchEnd = timeToMinutes("14:00");
  if (departMins < lunchEnd && arrivalMins > lunchStart) {
    return 60;
  }
  return 0;
};

// Get estimated duration based on device type
export const getEstimatedDuration = (deviceType, vehicleCategory) => {
  // Use allDeviceTypes (includes legacy combos) so durations of historical appointments stay correct
  const device = allDeviceTypes.find(d => d.value === deviceType);
  if (!device) return null;
  const extra = vehicleCategories.find(v => v.value === vehicleCategory);
  return device.duration + (extra ? extra.extraMinutes : 0);
};

// Format duration in hours and minutes
export const formatDuration = (minutes) => {
  if (!minutes) return "0 min";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
};

// Build a human-readable breakdown of how the total duration is computed.
// Format examples:
//   "SVR 1h + ASSIST-B 30 min + Moto 1h = 2h 30min × 4 mezzi = 10h"   (all vehicles identical)
//   "Mezzo 1: SVR 1h + Moto 1h = 2h | Mezzo 2: LMU3040 30 min + Moto 1h = 1h 30min = 3h 30min"
export const buildDurationBreakdown = (installations, vehicleCategory) => {
  if (!installations || installations.length === 0) return '';
  const vehicle = vehicleCategories.find(v => v.value === vehicleCategory);
  const vehicleBonus = vehicle ? vehicle.extraMinutes : 0;
  const vehicleLabel = vehicle ? vehicle.label : 'Auto';

  const perInstall = installations.map(inst => {
    const parts = [];
    let sum = 0;
    if (inst.device_type) {
      const d = allDeviceTypes.find(x => x.value === inst.device_type);
      if (d) {
        parts.push(`${d.label} ${formatDuration(d.duration)}`);
        sum += d.duration;
      }
    }
    (inst.extra_jobs || []).forEach(ej => {
      if (ej.device_type) {
        const d = allDeviceTypes.find(x => x.value === ej.device_type);
        if (d) {
          parts.push(`${d.label} ${formatDuration(d.duration)}`);
          sum += d.duration;
        }
      }
    });
    const hasAny = !!inst.device_type || (inst.extra_jobs || []).some(ej => !!ej.device_type);
    if (hasAny && vehicleBonus > 0) {
      parts.push(`${vehicleLabel} ${formatDuration(vehicleBonus)}`);
      sum += vehicleBonus;
    }
    return { parts, sum, key: parts.join('|') };
  }).filter(p => p.parts.length > 0);

  if (perInstall.length === 0) return '';

  const allIdentical = perInstall.length > 1 && perInstall.every(p => p.key === perInstall[0].key);
  if (allIdentical) {
    const p = perInstall[0];
    const total = p.sum * perInstall.length;
    return `${p.parts.join(' + ')} = ${formatDuration(p.sum)} × ${perInstall.length} mezzi = ${formatDuration(total)}`;
  }

  if (perInstall.length === 1) {
    return `${perInstall[0].parts.join(' + ')} = ${formatDuration(perInstall[0].sum)}`;
  }

  const lines = perInstall.map((p, i) => `Mezzo ${i + 1}: ${p.parts.join(' + ')} = ${formatDuration(p.sum)}`);
  const grandTotal = perInstall.reduce((acc, p) => acc + p.sum, 0);
  return `${lines.join(' | ')} = ${formatDuration(grandTotal)}`;
};

// Calculate travel time between two addresses using OSRM
export const calculateTravelTime = async (fromAddress, toAddress) => {
  try {
    const geocodeAddress = async (address) => {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
      const data = await res.json();
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
      }
      return null;
    };

    const from = await geocodeAddress(fromAddress);
    const to = await geocodeAddress(toAddress);
    if (!from || !to) return 30;

    const routeRes = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${from.lon},${from.lat};${to.lon},${to.lat}?overview=false`
    );
    const routeData = await routeRes.json();
    if (routeData.routes && routeData.routes.length > 0) {
      return Math.ceil(routeData.routes[0].duration / 60);
    }
    return 30;
  } catch (e) {
    console.error("Error calculating travel time:", e);
    return 30;
  }
};

// Group teams by country for dropdown
export const groupTeamsByCountry = (teams) => {
  return teams.reduce((acc, team) => {
    const country = team.country || 'Italia';
    if (!acc[country]) acc[country] = [];
    acc[country].push(team);
    return acc;
  }, {});
};

// Country labels for select dropdown
export const getCountryLabel = (country) => {
  const labels = {
    'Italia': 'Italia',
    'Francia': 'Francia',
    'Spagna': 'Spagna',
    'UK': 'Regno Unito'
  };
  return labels[country] || country;
};

export { API };
